import { CheckOutlined, DownOutlined } from "@ant-design/icons";
import { Button, Dropdown, Input, theme } from "../ui";
import type { MenuProps } from "../ui";
import { useMemo, useState, type CSSProperties } from "react";
import type { AppShellContextSelection, AppShellContextOption } from "./appShellContext";
import { measureTextWidthWithFont } from "../utils/fontMeasureContext";
import { appShellMetrics, spacing } from "./spacing";
import { AppShellStatusDot } from "./AppShellStatusDot";
import { AttentionIndicator } from "../ui/AttentionIndicator";
import { useUiText } from "../ui/uiText";

/** Ab so vielen Einträgen erscheint ein Suchfeld über der Liste. */
const searchThreshold = 8;
const recentLimit = 3;

const readRecent = (key?: string): string[] => {
  if (!key) return [];
  try {
    const parsed = JSON.parse(localStorage.getItem(key) ?? "[]");
    return Array.isArray(parsed) ? parsed.filter((value): value is string => typeof value === "string") : [];
  } catch {
    return [];
  }
};

const writeRecent = (key: string | undefined, value: string) => {
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify([value, ...readRecent(key).filter((entry) => entry !== value)].slice(0, recentLimit)));
  } catch {
    // Ohne Browser-Speicher bleibt die Reihenfolge unverändert.
  }
};

/** Existing title-bar dropdown pattern: text measurement affects width, never control height. */
export function AppShellContextSelector({ label, ariaLabel, value, options, marker, onChange, recentStorageKey, presentation = "dropdown" }: AppShellContextSelection & { presentation?: "dropdown" | "list" }) {
  const { token } = theme.useToken();
  const { text } = useUiText();
  const [query, setQuery] = useState("");
  const [recent, setRecent] = useState(() => readRecent(recentStorageKey));
  // Sammeleinträge (z. B. „alle Projekte“) bleiben vorn, danach zuletzt genutzte, dann der Rest in Originalreihenfolge.
  const orderedOptions = useMemo(() => {
    const recentRank = (entry: AppShellContextOption) => {
      const rank = recent.indexOf(entry.value);
      return rank < 0 ? recentLimit : rank;
    };
    return options
      .map((entry, index) => ({ entry, index }))
      .sort((left, right) => Number(Boolean(right.entry.aggregate)) - Number(Boolean(left.entry.aggregate))
        || recentRank(left.entry) - recentRank(right.entry)
        || left.index - right.index)
      .map(({ entry }) => entry);
  }, [options, recent]);
  const searchable = options.length >= searchThreshold;
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const visibleOptions = normalizedQuery
    ? orderedOptions.filter((entry) => entry.label.toLocaleLowerCase().includes(normalizedQuery))
    : orderedOptions;
  const choose = (next: string) => {
    writeRecent(recentStorageKey, next);
    setRecent(readRecent(recentStorageKey));
    setQuery("");
    onChange(next);
  };
  const counted = marker === "status" && options.some(option => (option.statusCount ?? 0) > 0);
  const markerWidth = marker === "check" ? appShellMetrics.contextCheckMarkerWidth : marker === "status" ? counted ? appShellMetrics.contextStatusCountWidth : appShellMetrics.contextStatusDotWidth : 0;
  const renderLabel = (option: AppShellContextOption, menuItem = false) => <span className="mq-app-context-option" style={{ display: "inline-flex", alignItems: "center", gap: spacing.tightGap }}>
    {marker === "check" || (menuItem && value !== undefined) ? <span style={{ width: appShellMetrics.contextCheckMarkerWidth, display: "inline-flex", justifyContent: "center" }}>{(option.checked ?? option.value === value) ? <CheckOutlined /> : null}</span> : null}
    <span className="mq-app-context-option-label">{option.label}</span>
    {marker === "status" ? option.status && option.statusCount != null
      ? <AttentionIndicator tone={option.status} label={option.statusLabel} count={option.statusCount} className="mq-frame-count" />
      : <AppShellStatusDot tone={option.status} label={option.statusLabel} /> : null}
  </span>;
  const items: MenuProps["items"] = [];
  for (const [index, option] of visibleOptions.entries()) {
    const item = { key: option.value, label: renderLabel(option, true) };
    items.push(item);
    if (option.aggregate && index < visibleOptions.length - 1) items.push({ type: "divider", key: `divider:${option.value}` });
  }
  const selectedOption = options.find(option => option.value === value);
  const width = useMemo(() => {
    // Theme font is available on the first render; do not sample another page button.
    const font = `400 ${token.fontSize}px ${token.fontFamily}`;
    const sizingLabels = options.length ? options.map(option => option.label) : [label];
    const widest = sizingLabels.reduce((max, text) => Math.max(max, measureTextWidthWithFont(font, text, appShellMetrics.contextFallbackCharacterWidth)), 0);
    const required = appShellMetrics.contextButtonPadding + markerWidth + spacing.tightGap + widest + spacing.tightGap + appShellMetrics.contextArrowWidth;
    return Math.min(appShellMetrics.contextMaxWidth, Math.ceil(required));
  }, [options, label, markerWidth, token.fontSize, token.fontFamily]);
  const labelMaxWidth = width - appShellMetrics.contextButtonPadding - spacing.tightGap - appShellMetrics.contextArrowWidth;
  if (presentation === "list") return <div className="mq-app-context-choices" role="group" aria-label={ariaLabel}>
    {searchable ? <Input allowClear placeholder={text("Search")} aria-label={text("Search")} value={query} onChange={event => setQuery(event.target.value)} /> : null}
    {visibleOptions.map(option => <Button key={option.value} block type="text" className="mq-app-context-choice"
      aria-pressed={option.checked ?? option.value === value} onClick={() => choose(option.value)}>{renderLabel(option, true)}</Button>)}
  </div>;
  return <Dropdown
    trigger={["click"]}
    menu={{ items, selectedKeys: [], onClick: ({ key }) => choose(String(key)) }}
    onOpenChange={(open) => { if (!open) setQuery(""); }}
    popupRender={searchable ? (menu) => (
      <div className="mq-app-context-search-popup">
        <Input
          autoFocus
          allowClear
          size="small"
          placeholder={text("Search")}
          aria-label={text("Search")}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => event.stopPropagation()}
        />
        {menu}
      </div>
    ) : undefined}
  >
    {/* Pfeil als Knopf-Icon statt als Kind: Ein Kit, das Kinder in ein Label-Element einschließt,
        setzte ein Kind-Icon ohne Abstand in den Textfluss. */}
    <Button className="mq-app-context-selector-button" icon={<DownOutlined />} iconPlacement="end" title={label} aria-label={ariaLabel} style={{ width, "--mq-shell-selector-narrow-max-width": `${appShellMetrics.contextNarrowMaxWidth}px`, "--mq-shell-selector-narrow-height": `${appShellMetrics.contextNarrowHeight}px`, "--mq-shell-selector-narrow-padding": `${appShellMetrics.contextNarrowPaddingInline}px` } as CSSProperties}>
      <span className="mq-app-context-selector-label" style={{ maxWidth: labelMaxWidth, lineHeight: `${appShellMetrics.contextLabelLineHeight}px` }}>{marker === "status" && selectedOption ? renderLabel(selectedOption) : label}</span>
    </Button>
  </Dropdown>;
}
